// client/src/contexts/AudioContext.jsx

import React, { createContext, useState, useEffect, useRef } from 'react';
import backgroundMusic from '../assets/sounds/musics/lost-among-the-stars.mp3';
import battleMusic from '../assets/sounds/musics/marche-des-soldats-de-la-galaxie.mp3';

export const AudioContext = createContext();

export const AudioProvider = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [currentMusicSource, setCurrentMusicSource] = useState('dashboard'); // dashboard, battle, etc.
  const [musicTransitioning, setMusicTransitioning] = useState(false);
  
  const audioRef = useRef(null);
  const userInteractedRef = useRef(false);
  
  // Initialiser l'audio de manière sécurisée
  useEffect(() => {
    const initializeAudio = () => {
      try {
        console.log("[AUDIO] Initialisation de l'audio...");
        
        // Créer une nouvelle instance Audio
        const audio = new Audio();
        
        // Configurer les gestionnaires d'événements
        audio.addEventListener('error', (e) => {
          console.error("[AUDIO] Erreur audio:", e);
          setAudioError(true);
        });
        
        audio.addEventListener('canplaythrough', () => {
          console.log("[AUDIO] Audio chargé et prêt à être joué");
        });
        
        audio.addEventListener('play', () => {
          console.log("[AUDIO] Audio en lecture");
          setIsPlaying(true);
        });
        
        audio.addEventListener('pause', () => {
          console.log("[AUDIO] Audio en pause");
          setIsPlaying(false);
        });
        
        // Configurer l'audio
        audio.loop = true;
        audio.autoplay = false;
        audio.preload = 'none';
        audio.volume = volume;
        
        // NE PAS définir src ni appeler load() ici
        // La source sera définie quand l'utilisateur demandera à jouer
        // Cela évite les requêtes réseau inutiles et les ERR_ABORTED
        
        // Stocker la référence
        audioRef.current = audio;
        setAudioInitialized(true);
        
        console.log("[AUDIO] Initialisation audio terminée (src différée)");
      } catch (error) {
        console.error("[AUDIO] Erreur lors de l'initialisation audio:", error);
        setAudioError(true);
      }
    };
    
    // N'initialiser qu'une seule fois
    if (!audioRef.current && !audioError) {
      initializeAudio();
    }
    
    // Nettoyer lors du démontage
    return () => {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.src = '';
          audioRef.current.removeAttribute('src');
          audioRef.current = null;
        } catch (error) {
          console.warn("[AUDIO] Erreur lors du nettoyage audio:", error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioError]);
  
  // Mettre à jour le volume lorsqu'il change
  useEffect(() => {
    if (audioRef.current && audioInitialized) {
      try {
        audioRef.current.volume = volume;
        console.log("[AUDIO] Volume audio mis à jour:", volume);
      } catch (error) {
        console.warn("[AUDIO] Erreur lors de la mise à jour du volume:", error);
      }
    }
  }, [volume, audioInitialized]);
  
  // Fonction pour jouer la musique
  const playMusic = () => {
    if (audioRef.current && audioInitialized && !audioError && !musicTransitioning) {
      try {
        console.log("[AUDIO] Tentative de lecture audio...");
        userInteractedRef.current = true;
        
        // Définir la source au premier appel (lazy loading)
        if (!audioRef.current.src) {
          const src = currentMusicSource === 'battle' ? battleMusic : backgroundMusic;
          audioRef.current.src = src;
          audioRef.current.load();
        }
        
        // Pour Chrome et autres navigateurs qui bloquent l'autoplay
        const playPromise = audioRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log("[AUDIO] Lecture audio démarrée avec succès");
              setIsPlaying(true);
            })
            .catch(error => {
              console.warn("[AUDIO] Lecture audio bloquée:", error);
              
              // Pour les navigateurs qui exigent une interaction utilisateur
              const unlockAudio = () => {
                console.log("[AUDIO] Tentative de déblocage audio suite à une interaction utilisateur");
                audioRef.current.play()
                  .then(() => {
                    console.log("[AUDIO] Audio débloqué avec succès");
                    setIsPlaying(true);
                    document.removeEventListener('click', unlockAudio);
                    document.removeEventListener('touchstart', unlockAudio);
                    document.removeEventListener('keydown', unlockAudio);
                  })
                  .catch(e => console.error("[AUDIO] Échec du déblocage audio:", e));
              };
              
              // Ajouter des écouteurs temporaires pour débloquer l'audio
              document.addEventListener('click', unlockAudio, { once: true });
              document.addEventListener('touchstart', unlockAudio, { once: true });
              document.addEventListener('keydown', unlockAudio, { once: true });
            });
        }
      } catch (error) {
        console.error("[AUDIO] Erreur lors de la lecture audio:", error);
      }
    } else {
      console.warn("[AUDIO] Impossible de lire l'audio: non initialisé ou en erreur ou en transition");
    }
  };
  
  // Fonction pour mettre en pause la musique
  const pauseMusic = () => {
    if (audioRef.current && audioInitialized && !audioError) {
      try {
        console.log("[AUDIO] Mise en pause audio...");
        audioRef.current.pause();
        setIsPlaying(false);
      } catch (error) {
        console.warn("[AUDIO] Erreur lors de la mise en pause audio:", error);
      }
    }
  };
  
  // Fonction pour basculer l'état de lecture
  const toggleMusic = () => {
    isPlaying ? pauseMusic() : playMusic();
  };
  
  // Fonction pour ajuster le volume
  const adjustVolume = (newVolume) => {
    setVolume(Math.max(0, Math.min(1, newVolume))); // Assurer que le volume est entre 0 et 1
  };
  
  // Fonction pour changer de musique
  const changeMusic = (musicPath, fadeOutTime = 500) => {
    if (audioError || musicTransitioning) return;
    
    try {
      if (audioRef.current) {
        console.log(`[AUDIO] Changement de musique vers:`, musicPath);
        setMusicTransitioning(true);
        
        // Si c'est la même musique, ne rien faire
        if (audioRef.current.src === musicPath) {
          setMusicTransitioning(false);
          return;
        }
        
        // Option 1: Transition douce (fade out/in)
        if (fadeOutTime > 0 && isPlaying) {
          // Réduire progressivement le volume
          const originalVolume = audioRef.current.volume;
          const fadeStep = originalVolume / (fadeOutTime / 50);
          let currentVolume = originalVolume;
          
          const fadeInterval = setInterval(() => {
            currentVolume = Math.max(0, currentVolume - fadeStep);
            audioRef.current.volume = currentVolume;
            
            if (currentVolume <= 0.01) {
              clearInterval(fadeInterval);
              
              // Changer la source une fois le volume à zéro
              audioRef.current.pause();
              audioRef.current.src = musicPath;
              audioRef.current.load();
              
              // Remonter progressivement le volume
              audioRef.current.volume = 0;
              
              const playPromise = audioRef.current.play();
              if (playPromise !== undefined) {
                playPromise.then(() => {
                  let fadeInVolume = 0;
                  
                  const fadeInInterval = setInterval(() => {
                    fadeInVolume = Math.min(originalVolume, fadeInVolume + fadeStep);
                    audioRef.current.volume = fadeInVolume;
                    
                    if (fadeInVolume >= originalVolume) {
                      clearInterval(fadeInInterval);
                      setMusicTransitioning(false);
                    }
                  }, 50);
                }).catch(e => {
                  console.warn("[AUDIO] Erreur lors du changement de musique:", e);
                  audioRef.current.volume = originalVolume;
                  setMusicTransitioning(false);
                });
              }
            }
          }, 50);
        } 
        // Option 2: Changement immédiat
        else {
          audioRef.current.pause();
          audioRef.current.src = musicPath;
          audioRef.current.load();
          
          if (isPlaying) {
            const playPromise = audioRef.current.play();
            if (playPromise !== undefined) {
              playPromise.then(() => {
                setMusicTransitioning(false);
              }).catch(e => {
                console.warn("[AUDIO] Erreur lors du changement de musique:", e);
                setMusicTransitioning(false);
              });
            }
          } else {
            setMusicTransitioning(false);
          }
        }
      }
    } catch (error) {
      console.warn("[AUDIO] Erreur lors du changement de musique:", error);
      setMusicTransitioning(false);
    }
  };
  
  // Fonction pour forcer la reprise du contexte audio
  const resumeAudioContext = () => {
    if (audioRef.current && !audioError) {
      console.log("[AUDIO] Tentative de reprise du contexte audio...");
      
      // Marquer l'interaction utilisateur
      userInteractedRef.current = true;
      
      // Forcer une interaction avec l'audio
      const silentPlay = () => {
        const originalVolume = audioRef.current.volume;
        audioRef.current.volume = 0.001; // Volume quasi-inaudible
        
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Remettre le volume d'origine après un court instant
              setTimeout(() => {
                audioRef.current.volume = originalVolume;
                if (!isPlaying) {
                  audioRef.current.pause();
                }
                console.log("[AUDIO] Contexte audio réveillé avec succès");
              }, 10);
            })
            .catch(error => {
              console.warn("[AUDIO] Échec de la reprise du contexte audio:", error);
              // Restaurer le volume
              audioRef.current.volume = originalVolume;
            });
        }
      };
      
      silentPlay();
    }
  };
  
  // Fonction pour vérifier et redémarrer la musique si elle s'est arrêtée
  const checkAndRestartAudio = () => {
    if (audioRef.current && isPlaying && audioRef.current.paused) {
      console.log("[AUDIO] Détection d'une interruption audio, tentative de redémarrage...");
      
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn("[AUDIO] Échec du redémarrage audio:", error);
        });
      }
    }
  };
  
  // Fonction pour démarrer la musique du dashboard
  const startDashboardMusic = () => {
    if (audioError || musicTransitioning) return;
    setMusicTransitioning(true);
    
    console.log("[AUDIO] Démarrage de la musique du dashboard");
    
    try {
      // Si on joue déjà cette musique, ne rien faire
      if (currentMusicSource === 'dashboard' && isPlaying && audioRef.current.src.includes('lost-among-the-stars')) {
        console.log("[AUDIO] Musique du dashboard déjà en lecture");
        setMusicTransitioning(false);
        return;
      }
      
      // Changer pour la musique du dashboard
      if (audioRef.current) {
        // D'abord, arrêter la musique en cours
        audioRef.current.pause();
        
        // Changer la source
        audioRef.current.src = backgroundMusic;
        audioRef.current.load();
        
        // Mettre à jour l'état
        setCurrentMusicSource('dashboard');
        
        // Démarrer la lecture avec un volume progressif
        audioRef.current.volume = 0;
        
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            // Augmenter progressivement le volume
            let vol = 0;
            const targetVol = volume;
            const fadeInterval = setInterval(() => {
              vol = Math.min(targetVol, vol + 0.05);
              audioRef.current.volume = vol;
              
              if (vol >= targetVol) {
                clearInterval(fadeInterval);
                setMusicTransitioning(false);
              }
            }, 50);
            
            setIsPlaying(true);
          }).catch(e => {
            console.warn("[AUDIO] Erreur lors du démarrage de la musique dashboard:", e);
            setMusicTransitioning(false);
          });
        }
      }
    } catch (error) {
      console.error("[AUDIO] Erreur lors du démarrage de la musique dashboard:", error);
      setMusicTransitioning(false);
    }
  };
  
  // Fonction pour démarrer la musique de bataille
  const startBattleMusic = () => {
    if (audioError || musicTransitioning) return;
    setMusicTransitioning(true);
    
    console.log("[AUDIO] Démarrage de la musique de bataille");
    
    try {
      // Si on joue déjà cette musique, ne rien faire
      if (currentMusicSource === 'battle' && isPlaying) {
        console.log("[AUDIO] Musique de bataille déjà en lecture");
        setMusicTransitioning(false);
        return;
      }
      
      // Changer pour la musique de bataille
      if (audioRef.current) {
        // D'abord, faire un fade-out sur la musique actuelle
        const originalVolume = audioRef.current.volume;
        let vol = originalVolume;
        
        const fadeOutInterval = setInterval(() => {
          vol = Math.max(0, vol - 0.05);
          if (audioRef.current) audioRef.current.volume = vol;
          
          if (vol <= 0) {
            clearInterval(fadeOutInterval);
            
            // Arrêter la musique actuelle
            if (audioRef.current) {
              audioRef.current.pause();
              
              // Changer la source
              audioRef.current.src = battleMusic;
              audioRef.current.load();
              
              // Mettre à jour l'état
              setCurrentMusicSource('battle');
              
              // Démarrer la lecture avec un volume progressif
              audioRef.current.volume = 0;
              
              const playPromise = audioRef.current.play();
              if (playPromise !== undefined) {
                playPromise.then(() => {
                  // Augmenter progressivement le volume
                  let newVol = 0;
                  const fadeInInterval = setInterval(() => {
                    newVol = Math.min(originalVolume, newVol + 0.05);
                    audioRef.current.volume = newVol;
                    
                    if (newVol >= originalVolume) {
                      clearInterval(fadeInInterval);
                      setMusicTransitioning(false);
                    }
                  }, 50);
                  
                  setIsPlaying(true);
                }).catch(e => {
                  console.warn("[AUDIO] Erreur lors du démarrage de la musique de bataille:", e);
                  setMusicTransitioning(false);
                });
              }
            }
          }
        }, 50);
      }
    } catch (error) {
      console.error("[AUDIO] Erreur lors du démarrage de la musique de bataille:", error);
      setMusicTransitioning(false);
    }
  };

  return (
    <AudioContext.Provider
      value={{
        isPlaying,
        volume,
        currentMusicSource,
        playMusic,
        pauseMusic,
        toggleMusic,
        adjustVolume,
        resumeAudioContext,
        changeMusic,
        checkAndRestartAudio,
        startDashboardMusic,
        startBattleMusic,
        hasError: audioError,
        isInitialized: audioInitialized
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};